import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Editor, EditorState, RichUtils, convertFromRaw, convertToRaw } from "draft-js";
import { Translate } from '../../i18n/i18n';
import "draft-js/dist/Draft.css";

const Container = styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
`;

const ButtonBar = styled.div`
    width: 100%;
    height: 38px;
    border-bottom: 1px solid #444;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding-left: 5px;
`;

const Separator = styled.div`
    width: 1px;
    height: 30px;
    margin: 0 5px;
    background-color: #444;
`;

const EditorWrapper = styled.div`
    top: 39px;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;

    &.readonly {
        top: 0;
    }
`;

const OpButton = styled.div`
    width: 30px;
    height: 30px;
    border: 1px solid #666;
    color: #aaa;
    font-size: 18px;
    position: relative;
    z-index: 1;
    cursor: pointer;
    background-color: #000;
    transition: all .4s ease 0s;
    margin-left: -1px;

    :hover {
        border-color: #fff;
        color: #fff;
        z-index: 2;
    }

    &:before {
        content: '${p => p.icon}';
        font-family: 'Material Design Icons';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }
`;

export default ({onChange, value, readOnly}) => {
    const [lastValue, setLastValue] = useState(null);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    
    const editor = React.useRef(null);

    useEffect(() => {
        if (value && value !== lastValue) {
            let obj = null;
            try {
                obj = JSON.parse(value);
            } catch (e) {
                obj = null;
            }

            setLastValue(value);

            if (obj && obj.blocks) {
                const v = convertFromRaw(obj);
                setEditorState(EditorState.createWithContent(v));
            }
        }
    }, [value, lastValue]);

    useEffect(() => {
        if (typeof onChange === 'function') {
            onChange(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
        }
    }, [onChange, editorState]);

    const focusEditor = () => {
        editor.current.focus();
    };

    const toggleInlineStyle = (style) => {
        setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    }

    const toggleBlockType = (type) => {
        setEditorState(RichUtils.toggleBlockType(editorState, type));
    }

    const handleKeyCommand = (command, editorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
    
        if (newState) {
            setEditorState(newState);
            return 'handled';
        }
    
        return 'not-handled';
    };

    return <Container>
        {!readOnly ?
            <ButtonBar>
                <OpButton icon='\F0264' onClick={()=>{toggleInlineStyle('BOLD')}} title={Translate('texteditor.bold')}/>
                <OpButton icon='\F0277' onClick={()=>{toggleInlineStyle('ITALIC')}} title={Translate('texteditor.italic')}/>
                <OpButton icon='\F0287' onClick={()=>{toggleInlineStyle('UNDERLINE')}} title={Translate('texteditor.underline')}/>
                <Separator />
                <OpButton icon='\F026B' onClick={(e) => {toggleBlockType('header-one')}} title={Translate('texteditor.h1')}/>
                <OpButton icon='\F026C' onClick={(e) => {toggleBlockType('header-two')}} title={Translate('texteditor.h2')}/>
                <OpButton icon='\F026D' onClick={(e) => {toggleBlockType('header-three')}} title={Translate('texteditor.h3')}/>
                <OpButton icon='\F0279' onClick={(e) => {toggleBlockType('unordered-list-item')}} title={Translate('texteditor.list')}/>
                <OpButton icon='\F027E' onClick={(e) => {toggleBlockType('blockquote')}} title={Translate('texteditor.quote')}/>
            </ButtonBar>
        : null }
        <EditorWrapper className={'scrollbar' + (readOnly ? ' readonly' : '')} onClick={focusEditor}>
            <Editor
                ref={editor}
                readOnly={readOnly}
                editorState={editorState}
                onChange={setEditorState}
                handleKeyCommand={handleKeyCommand}
                placeholder={Translate('assets.defaultTextContent')}
            />
        </EditorWrapper>
    </Container>
}